import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { NAV, findPageMeta } from "@/nav";
import { ChevronLeft, ChevronRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const renderParagraphs = (body) =>
  body.split(/\n\n+/).map((chunk, i) => {
    // preserve inline newlines as <br /> for numbered lists / quoted lines
    const lines = chunk.split("\n");
    return (
      <p key={i}>
        {lines.map((line, li) => (
          <React.Fragment key={li}>
            {line}
            {li < lines.length - 1 && <br />}
          </React.Fragment>
        ))}
      </p>
    );
  });

const findAdjacent = (slug) => {
  const flat = NAV.flatMap((c) => c.items.map((i) => ({ ...i, cluster: c })));
  const idx = flat.findIndex((i) => i.slug === slug);
  return { prev: flat[idx - 1] || null, next: flat[idx + 1] || null };
};

const Page = () => {
  const { slug } = useParams();
  const [page, setPage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const meta = findPageMeta(slug);
  const { prev, next } = findAdjacent(slug);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(null);
    axios
      .get(`${API}/pages/${slug}`)
      .then((r) => mounted && setPage(r.data))
      .catch((e) => mounted && setError(e?.response?.status === 404 ? "Not found" : "Could not load this page."))
      .finally(() => mounted && setLoading(false));
    return () => { mounted = false; };
  }, [slug]);

  return (
    <article data-testid={`page-${slug}`} className="border-b border-line">
      {/* HEADER */}
      <header className="border-b border-line bg-paper">
        <div className="max-w-5xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
          {meta && (
            <p className="text-xs uppercase tracking-[0.28em] text-terracotta mb-6">
              <Link to={`/${meta.cluster.items[0].slug}`} className="hover:text-terracotta-hover">
                {meta.cluster.label}
              </Link>
            </p>
          )}
          {loading ? (
            <div className="h-10 w-2/3 bg-cream animate-pulse" data-testid="page-loading" />
          ) : error ? (
            <h1 className="font-serif text-4xl lg:text-5xl text-ink" data-testid="page-error">{error}</h1>
          ) : (
            <>
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight text-ink mb-6 animate-fade-up">
                {page.title}
              </h1>
              {page.subtitle && (
                <p className="text-lg lg:text-xl text-stone max-w-3xl leading-relaxed animate-fade-up" style={{ animationDelay: "60ms" }}>
                  {page.subtitle}
                </p>
              )}
            </>
          )}
        </div>
      </header>

      {/* BODY + SIDEBAR */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20 grid grid-cols-12 gap-8 lg:gap-16">
        <div className="col-span-12 lg:col-span-8 editorial-body">
          {slug === "agency-legitimacy" && <LegitimacyChecklistInline />}
          {slug === "safeguarding-contacts" && <SafeguardingInline />}
          {slug === "tools-resources" && <ContactBoxInline />}

          {!loading && !error && page?.sections?.map((section, i) => (
            <section key={i} className="mb-12 animate-fade-up" style={{ animationDelay: `${i * 60}ms` }}>
              <h2 className="font-serif text-2xl lg:text-3xl text-ink mb-5 leading-snug">
                {section.heading}
              </h2>
              <div className="text-[17px] text-ink/90 leading-[1.75]">
                {renderParagraphs(section.body)}
              </div>
            </section>
          ))}
        </div>

        {/* SIDEBAR */}
        <aside className="col-span-12 lg:col-span-3 lg:col-start-10">
          {meta && (
            <div className="border border-line p-6 mb-8 bg-cream/60 sticky top-24">
              <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-3">
                In this section
              </p>
              <ul className="space-y-2">
                {meta.cluster.items.map((item) => (
                  <li key={item.slug}>
                    <Link
                      to={`/${item.slug}`}
                      data-testid={`sidebar-${item.slug}`}
                      className={`block text-sm py-1 transition-colors ${
                        item.slug === slug ? "text-terracotta font-medium" : "text-ink hover:text-terracotta"
                      }`}
                      aria-current={item.slug === slug ? "page" : undefined}
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {/* PREV / NEXT */}
      {(prev || next) && (
        <nav aria-label="Related pages" className="border-t border-line bg-cream">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid grid-cols-2 gap-6">
            <div>
              {prev && (
                <Link to={`/${prev.slug}`} data-testid="page-prev" className="group inline-flex flex-col">
                  <span className="text-[11px] uppercase tracking-[0.24em] text-stone mb-2 inline-flex items-center gap-1">
                    <ChevronLeft size={12} /> Previous
                  </span>
                  <span className="font-serif text-lg text-ink group-hover:text-terracotta transition-colors">{prev.label}</span>
                </Link>
              )}
            </div>
            <div className="text-right">
              {next && (
                <Link to={`/${next.slug}`} data-testid="page-next" className="group inline-flex flex-col items-end">
                  <span className="text-[11px] uppercase tracking-[0.24em] text-stone mb-2 inline-flex items-center gap-1">
                    Next <ChevronRight size={12} />
                  </span>
                  <span className="font-serif text-lg text-ink group-hover:text-terracotta transition-colors">{next.label}</span>
                </Link>
              )}
            </div>
          </div>
        </nav>
      )}
    </article>
  );
};

// ---------------- Inline interactive fragments ----------------

const LegitimacyChecklistInline = () => {
  const [items, setItems] = useState([]);
  const [checked, setChecked] = useState({});

  useEffect(() => {
    const saved = JSON.parse(localStorage.getItem("nfg_legit_checks") || "{}");
    setChecked(saved);
    axios.get(`${API}/checklist/legitimacy`).then((r) => setItems(r.data.items || []));
  }, []);

  const toggle = (id) => {
    const next = { ...checked, [id]: !checked[id] };
    setChecked(next);
    localStorage.setItem("nfg_legit_checks", JSON.stringify(next));
  };

  const total = items.length;
  const done = items.filter((i) => checked[i.id]).length;
  const half = total > 0 && done < Math.ceil(total / 2);

  return (
    <section className="mb-14 border border-line p-6 lg:p-8 bg-cream/50" data-testid="legitimacy-checklist">
      <div className="flex items-baseline justify-between mb-6 gap-4 flex-wrap">
        <h2 className="font-serif text-2xl lg:text-3xl text-ink leading-snug m-0">The verification checklist</h2>
        <p className="text-sm text-stone" data-testid="legitimacy-progress">
          <span className="font-medium text-ink">{done}</span> of {total} confirmed
        </p>
      </div>
      <ul className="divide-y divide-line">
        {items.map((item) => (
          <li key={item.id} className="py-4 flex items-start gap-4">
            <button
              onClick={() => toggle(item.id)}
              data-testid={`legit-check-${item.id}`}
              aria-pressed={!!checked[item.id]}
              className={`mt-0.5 flex-shrink-0 w-5 h-5 border transition-colors flex items-center justify-center ${
                checked[item.id]
                  ? "bg-terracotta border-terracotta text-paper"
                  : "bg-paper border-ink/40 hover:border-ink"
              }`}
              aria-label={checked[item.id] ? "Uncheck" : "Check"}
            >
              {checked[item.id] && (
                <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
                  <path d="M2 6.5L5 9.5L10 3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              )}
            </button>
            <label
              onClick={() => toggle(item.id)}
              className={`text-[16px] leading-relaxed cursor-pointer select-none ${
                checked[item.id] ? "text-stone line-through" : "text-ink"
              }`}
            >
              {item.label}
            </label>
          </li>
        ))}
      </ul>
      {total > 0 && half && done > 0 && (
        <p className="mt-6 text-sm text-stone italic" data-testid="legit-warning">
          You've confirmed fewer than half. Treat this as a red flag and speak to a trusted adult
          before signing or attending any shoot.
        </p>
      )}
      <p className="mt-6 text-xs text-stone">Your ticks are saved on this device only.</p>
    </section>
  );
};

const SafeguardingInline = () => {
  const [contacts, setContacts] = useState([]);
  useEffect(() => {
    axios.get(`${API}/safeguarding/contacts`).then((r) => setContacts(r.data.contacts || []));
  }, []);

  const emergency = contacts.filter((c) => c.kind === "emergency");
  const rest = contacts.filter((c) => c.kind !== "emergency");

  return (
    <section className="mb-14" data-testid="safeguarding-list">
      {emergency.map((c) => (
        <div
          key={c.id}
          className="border-l-4 border-terracotta bg-terracotta/5 p-6 mb-8"
          data-testid={`safeguarding-${c.id}`}
        >
          <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-2">In the moment</p>
          <h3 className="font-serif text-2xl text-ink mb-2">{c.name}</h3>
          <p className="text-ink leading-relaxed">{c.detail}</p>
        </div>
      ))}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-px bg-line border border-line mb-4">
        {rest.map((c) => (
          <div key={c.id} className="bg-paper p-6" data-testid={`safeguarding-${c.id}`}>
            <p className="text-[11px] uppercase tracking-[0.24em] text-terracotta mb-2">{c.kind}</p>
            <h3 className="font-serif text-xl text-ink mb-2 leading-snug">{c.name}</h3>
            <p className="text-sm text-ink/85 leading-relaxed">{c.detail}</p>
          </div>
        ))}
      </div>
      <p className="text-xs text-stone italic mt-4">
        Phone numbers, opening hours, and services change. If a number here doesn't connect, search
        gov.uk or the charity's own website for the current details.
      </p>
    </section>
  );
};

const ContactBoxInline = () => {
  const [form, setForm] = useState({ name: "", email: "", role: "model", message: "" });
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setStatus("sending");
    setError(null);
    try {
      await axios.post(`${API}/contact`, form);
      setStatus("sent");
      setForm({ name: "", email: "", role: "model", message: "" });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Please try again.");
      setStatus("idle");
    }
  };

  return (
    <section className="mb-14 border border-line p-6 lg:p-8 bg-cream/50" data-testid="contact-form">
      <h2 className="font-serif text-2xl lg:text-3xl text-ink mb-3">Something we should add?</h2>
      <p className="text-sm text-stone mb-6 max-w-xl">
        If you're a working model, a parent, or a professional who spots something out of date or
        missing, drop us a note. We read everything but don't send marketing.
      </p>
      {status === "sent" ? (
        <p className="text-sm text-ink" data-testid="contact-success">Thank you — your note has arrived. We'll read it carefully.</p>
      ) : (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <input
            data-testid="contact-name"
            type="text"
            required
            placeholder="Your name"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="border border-line bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
          />
          <input
            data-testid="contact-email"
            type="email"
            required
            placeholder="Your email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="border border-line bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink"
          />
          <select
            data-testid="contact-role"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
            className="border border-line bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink md:col-span-2"
          >
            <option value="model">I'm a model</option>
            <option value="parent">I'm a parent or guardian</option>
            <option value="other">Something else</option>
          </select>
          <textarea
            data-testid="contact-message"
            required
            placeholder="What would you like to tell us?"
            value={form.message}
            onChange={(e) => setForm({ ...form, message: e.target.value })}
            rows={5}
            className="md:col-span-2 border border-line bg-paper px-4 py-3 text-sm focus:outline-none focus:border-ink resize-none"
          />
          <div className="md:col-span-2 flex items-center justify-between gap-4 flex-wrap">
            {error && <p className="text-sm text-terracotta" data-testid="contact-error">{error}</p>}
            <button
              type="submit"
              data-testid="contact-submit"
              disabled={status === "sending"}
              className="btn-terracotta px-6 py-3 rounded-full text-sm font-medium disabled:opacity-60"
            >
              {status === "sending" ? "Sending…" : "Send note"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
};

export default Page;
